import * as React from 'react';

import styled from 'styled-components';

import SimpleUser from '@common/types/user/simple-user';
import { normalizePhoneOrEmail } from '@webapp/utils/normalizers';
import { UserSearchResult } from '@webapp/types/user-search-result';

import Flex from '@components/layout/flex';
import PlusIcon from '@components/basics/icons/plus';
import UsersSearchCtrl from '../user-search/index';
import MotionOpacity from '@components/motion/motion-opacity';
import InviteUserInlineCtrl from '../../invites/invite-user-inline';

import { ContactInfo } from '@webapp/types/common/contact-info';

import { TracInviteTypes } from '@services/invites/types/invite-types';

interface UserInviteSearchCtrlProps {
  id?: string;
  className?: string;
  label: React.ReactNode;
  value?: SimpleUser;
  disableInvite?: boolean;
  type: TracInviteTypes;
  role: string;
  currentValues: SimpleUser[];
  locked?: boolean;
  decoration?: React.ReactNode;
  hasClose?: boolean;
  metadata?: Record<string, any>;
  onClose?: (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  onChange?: (value: string) => void;
  onSelection: (value: Partial<SimpleUser>, type: 'user' | 'invite') => void;
  setContact?: React.Dispatch<React.SetStateAction<ContactInfo | undefined>>;
  setName?: React.Dispatch<React.SetStateAction<string>>;
  leftDecoration?: React.ReactNode;
  customBorderRadius?: string;
}

export const UserInviteSearchCtrl: React.FC<UserInviteSearchCtrlProps> = (
  props
) => {
  const [showInvite, setShowInvite] = React.useState(false);
  const [disableInvite, setDisableInvite] = React.useState(false);
  const [value, setValue] = React.useState(
    props.value?.email || props.value?.telephone || ''
  );
  const { setName } = props;
  React.useEffect(() => setName && setName(value), [value, setValue, setName]);

  const handleChangeResults = () => {
    setShowInvite(!!value && !props.value?.id);
  };

  const handleSelection = (newValue: UserSearchResult) => {
    const contact = normalizePhoneOrEmail(newValue.contact);

    if (contact) {
      props.onSelection(
        {
          id: newValue.id,
          fullName: newValue.name,
          email: contact.type === 'email' ? contact.value : undefined,
          telephone: contact.type === 'sms' ? contact.value : undefined,
        },
        newValue.type
      );
      setDisableInvite(false);
      setValue(contact.value);
    }
    setValue('');
    setShowInvite(false);
  };

  const handleChange = (newValue: string) => {
    setValue(newValue);
    if (props.onChange) {
      props.onChange(newValue);
    }
  };

  return (
    <MotionOpacity className={props.className}>
      <Flex
        className={`input-wrapper ${
          showInvite && !disableInvite ? 'mb-10' : ''
        }`}
        style={{ zIndex: 11 }}
        gap={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <UsersSearchCtrl
          id={props.id}
          className="input-split"
          label={props.label}
          value={value}
          type={props.type}
          currentValues={props.currentValues}
          locked={props.locked}
          decoration={props.decoration}
          hasClose={props.hasClose}
          onClose={props.onClose}
          onChange={(newValue) => {
            setDisableInvite(false);
            handleChange(newValue);
          }}
          onSelection={(newValue) => handleSelection(newValue)}
          onResultsChange={() => handleChangeResults()}
          parentRelative={true}
          noShadow={true}
          customBorderRadius={`${props.customBorderRadius} || "4px 0 0 4px"`}
          hasTruncate
          leftDecoration={
            props.leftDecoration || (
              <CircleIcon>
                <PlusIcon isWhite />
              </CircleIcon>
            )
          }
        />
        {props.children}
      </Flex>
      {showInvite && !disableInvite ? (
        <MotionOpacity>
          <InviteUserInlineCtrl
            name={value}
            metadata={props.metadata}
            role={props.role}
            type={props.type}
            disableInvite={props.disableInvite}
            onInvite={(user) => handleSelection(user)}
            setContact={props.setContact}
            onCancel={() => {
              setValue('');
              setShowInvite(false);
            }}
          />
        </MotionOpacity>
      ) : null}
    </MotionOpacity>
  );
};

export default styled(UserInviteSearchCtrl)`
  .input-wrapper {
    position: relative;
    z-index: 10;

    & > * {
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 35%;

      &:first-child {
        flex-basis: 65%;
      }

      ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
        flex-basis: 30%;

        &:first-child {
          flex-basis: 70%;
        }
      }
    }
  }
`;
const CircleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => theme.colors.primary};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    .icon {
      path {
        fill: ${({ theme }) => theme.colors.differentBackground};
      }
    }
  }
`;
